
import { defineComponent, nextTick } from "vue";
import store from "@/store";

import StudentsService from "@/api/Aula/StudentsService";
import TeachersService from "@/api/Aula/TeachersService";
import IndividualEnrollmentService from "@/api/Enrollment/IndividualEnrollmentService";

import PreschoolTeacherAssignmentService from "@/api/Preschool/PreschoolTeacherAssignmentService";
import PeriodsService from "@/api/Configuration/PeriodsService";
import SystemEvaluationMetricsService from "@/api/Academic/SystemEvaluationMetricsService";
import CourseGroupsService from "@/api/Aula/CourseGroupsService";
import PreschoolDimensionsService from "@/api/Preschool/PreschoolDimensionsService";
import PreschoolPerformancePeriodAssignmentsService from "@/api/Preschool/PreschoolPerformancePeriodAssignmentsService";
import PreschoolPerformancesService from "@/api/Preschool/PreschoolPerformancesService";
import PreschoolEvaluationsService from "@/api/Preschool/PreschoolEvaluationsService";

import type {
  IStudentsModel,
  IPreschoolDimensionsModel,
  ISystemEvaluationMetricsModel,
  IIndividualEnrollmentModel,
  IPeriodsModel,
  ICourseGroupsModel,
  ITeachersModel,
  IPreschoolPerformancesModel,
  IPreschoolEvaluationsModel
} from "@/interfaces/modules";

type dimensionCode = string;

type FieldDef = {
  key: string;
  label?: string;
  thClass?: string | string[];
  tdClass?: string | string[];
  thStyle?: Record<string, string>;
  thAttr?: Record<string, string>;
  stickyColumn?: boolean;
};

type DimensionUi = IPreschoolDimensionsModel & {
  performances: Array<IPreschoolPerformancesModel & { percent: number; DisplayCode: string }>;
  color?: string;
};

export default defineComponent({
  name: "AcademicEvaluation",

  data() {
    return {
      /* resolved from the assignment chain */
      assignmentId: this.$route.params.Id || 0,
      academicYearId: 0,
      subjectId: 0,
      courseGroupId: 0,
      teacherId: 0,

      /* periods */
      periodId: 0, // current period (for edit gate UI)
      periods: [] as IPeriodsModel[],
      selectedPeriodId: "" as string, // "" = ALL
      selectedPeriodIds: [] as number[], // multi-select support for period chips
      teacher: {} as ITeachersModel,
      loading: false,
      saving: false,

      canEdit: false,
      periodGate: {
        id: 0,
        number: 0,
        description: "",
        openFrom: null as string | null,
        openTo: null as string | null,
      },

      /* students */
      students: [] as IStudentsModel[],
      enrollments: [] as IIndividualEnrollmentModel[],
      groups: [] as ICourseGroupsModel[],

      photo: { open: false, url: null as string | null, initials: "", name: "" },

      /* sabers/competencies per period */
      baseDimensionList: [] as DimensionUi[],
      periodSaberMap: {} as Record<number, DimensionUi[]>,
      visibleDimensions: {} as Record<dimensionCode, boolean>,
      visiblePeriods: {} as Record<number, boolean>,
      studentNotes: {} as Record<number, { Observation: string; Recommendations: string }>,
      scoreIds: {} as Record<number, Record<string, number>>,

      /* ✅ FIX: estado persistente de detalles por estudiante */
      detailsOpen: {} as Record<number, boolean>,

      /* grades keyed by student and composed keys */
      scores: {} as Record<number, Record<string, number | string>>,

      /* legacy direct-score shown on Subtotal for performance-enabled sabers */
      legacySubtotals: {} as Record<number, Record<string, number>>,
      draft: { savedAt: null as Date | null, savedAtShort: "", savedAtTitle: "" },

      autosave: { enabled: true, minutes: 1 as number },
      autosaveTimer: null as any,

      navigatorOnline: typeof navigator !== "undefined" ? navigator.onLine : true,

      float: { visible: false, style: {} as Record<string, string> },

      metrics: [] as ISystemEvaluationMetricsModel[],
      minScore: null as number | null,
      maxScore: null as number | null,

      errors: {} as Record<string, string>,

      performancedimensionCode: "",
    };
  },

  computed: {
    periodOptions(): Array<{ value: string; text: string }> {
      const base = [{ value: "", text: "Todos" }];

      const opts = (this.periods || []).map((p: any) => {
        const pid = Number(p.Id);
        const baseLabel = `P${p.PeriodNumber ?? pid} — ${p.Description ?? "Sin descripción"}`;
        const isCurrent = this.periodId && pid === Number(this.periodId);
        return {
          value: String(pid),
          text: isCurrent ? `${baseLabel} (Actual)` : baseLabel,
        };
      });

      return base.concat(opts);
    },

    currentPeriodObj(): any | null {
      const pid = Number(this.periodId || 0);
      if (pid && this.periods && this.periods.length) {
        const direct = this.periods.find((p: any) => Number(p.Id) === pid);
        if (direct) return direct;
      }

      if (this.visiblePeriods) {
        const firstVisibleId = Object.keys(this.visiblePeriods)
          .map((k) => Number(k))
          .find((id) => this.visiblePeriods[id]);
        if (firstVisibleId) {
          return (this.periods || []).find((p: any) => Number(p.Id) === firstVisibleId) || null;
        }
      }

      return null;
    },

    currentPeriodLabel(): string {
      const p = this.currentPeriodObj;
      if (!p) return "Todos los períodos";
      const num = (p as any).PeriodNumber ?? p.Id;
      const desc = (p as any).Description ?? "";
      const base = `P${num}`;
      return desc ? `${base} — ${desc}` : base;
    },

    periodsToDraw(): Array<any> {
      return this.periods || [];
    },

    currentGroupName(): string {
      const _course = Number(this.courseGroupId);
      if (!_course) return '';
      const g = (this.groups || []).find(x => x.Id === this.courseGroupId);
      return g ? g.Name : '';
    },

    tableFields(): FieldDef[] {
      const base: FieldDef[] = [
        { key: "StudentName", label: "Estudiante", stickyColumn: true, thClass: "bg-white z-3", tdClass: "" },
      ];
      const dynamic: FieldDef[] = [];

      for (const p of this.periodsToDraw) {
        const periodId = Number(p.Id);
        if (!this.visiblePeriods[periodId]) continue;
        const dimensiones = this.periodSaberMap[periodId] || [];
        for (const s of dimensiones) {
          if (!this.visibleDimensions[s.Code]) continue;

          if (s.performances.length) {
            for (const c of s.performances) {
              const key = this.keyPerf(periodId, s.Code, c.Id);
              dynamic.push({
                key,
                label: `${c.Code} (${Number(c.percent) || 0}%)`,
                tdClass: "p-0",
                thClass: "p-0 text-center fw-semibold",
                thStyle: { background: this.compColor(c) || s.color || "" },
                thAttr: { title: (c as any).Description || (c as any).Title },
              });
            }
            const subKey = this.keySubtotal(periodId, s.Code);
            dynamic.push({
              key: subKey,
              label: `Final (100%)`,
              tdClass: "p-0",
              thClass: "p-0 text-center fw-semibold",
              thStyle: { background: s.color || "" },
              thAttr: { title: `${s.Name}: promedio ponderado de desempeños (las ponderaciones de desempeños suman 100%)` },
            });
          } else {
            const key = this.keyDirectSaber(periodId, s.Code);
            dynamic.push({
              key,
              label: `${s.Name}`,
              tdClass: "p-0",
              thClass: "p-0 text-center fw-semibold",
              thStyle: { background: s.color || "" },
              thAttr: { title: `${s.Name}: evaluación directa` },
            });
          }
        }
      }

      dynamic.push({
        key: "FinalScore",
        label: "Final (100%)",
        tdClass: "p-0",
        thClass: "p-0 text-center fw-bold text-white",
        thStyle: { background: "#198754" },
      });

      return base.concat(dynamic);
    },

    periodHeaderGroups(): Array<{ periodId: number; label: string; colspan: number; headerStyle: any }> {
      const groups: Array<{ periodId: number; label: string; colspan: number; headerStyle: any }> = [];

      for (const p of this.periodsToDraw) {
        const periodId = Number(p.Id);
        if (!this.visiblePeriods[periodId]) continue;

        const sabers = this.periodSaberMap[periodId] || [];
        let colCount = 0;

        for (const s of sabers) {
          if (!this.visibleDimensions[s.Code]) continue;
          colCount += s.performances.length ? s.performances.length + 1 : 1;
        }

        if (colCount > 0) {
          groups.push({
            periodId,
            label: `${p.PeriodNumber ?? p.Id}`,
            colspan: colCount,
            headerStyle: { background: "#eef5ff" },
          });
        }
      }
      return groups;
    },

    saberHeaderGroups(): Array<{ periodId: number; code: string; name: string; colspan: number; headerStyle: any }> {
      const groups: Array<{ periodId: number; code: string; name: string; colspan: number; headerStyle: any }> = [];

      for (const p of this.periodsToDraw) {
        const periodId = Number(p.Id);
        if (!this.visiblePeriods[periodId]) continue;
        const sabers = this.periodSaberMap[periodId] || [];

        for (const s of sabers) {
          if (!this.visibleDimensions[s.Code]) continue;

          const cols = s.performances.length ? s.performances.length + 1 : 1;
          groups.push({
            periodId,
            code: s.Code,
            name: s.Name,
            colspan: cols,
            headerStyle: { background: s.color || "" },
          });
        }
      }
      return groups;
    },

    dynamicValueFields(): Array<{ key: string }> {
      const arr: Array<{ key: string }> = [];

      for (const p of this.periodsToDraw) {
        const periodId = Number(p.Id);
        if (!this.visiblePeriods[periodId]) continue;

        const sabers = this.periodSaberMap[periodId] || [];
        for (const s of sabers) {
          if (!this.visibleDimensions[s.Code]) continue;

          if (s.performances.length) {
            for (const c of s.performances) arr.push({ key: this.keyPerf(periodId, s.Code, c.Id) });
          } else {
            arr.push({ key: this.keyDirectSaber(periodId, s.Code) });
          }
        }
      }
      return arr;
    },

    subtotalFields(): Array<{ key: string }> {
      const arr: Array<{ key: string }> = [];
      for (const p of this.periodsToDraw) {
        const periodId = Number(p.Id);
        if (!this.visiblePeriods[periodId]) continue;

        const sabers = this.periodSaberMap[periodId] || [];
        for (const s of sabers) {
          if (this.visibleDimensions[s.Code] && s.performances.length) {
            arr.push({ key: this.keySubtotal(periodId, s.Code) });
          }
        }
      }
      return arr;
    },

    tableItems(): Array<any> {
      return this.students.map((st) => {
        const name = `${st.Name || ""} ${st.LastName || ""}`.trim();
        const initials = (name.split(" ").map((p) => p[0]).join("").substring(0, 2) || "ST").toUpperCase();

        const row: any = {
          Id: st.Id,
          StudentName: name,
          Initials: initials,
          PhotoUrl: (st as any).Image || (st as any).PhotoUrl || null,
          Document: (st as any).IdentificationNumber || "",
          /* ✅ FIX: el row-details se controla por este flag, tomado del mapa persistente */
          _showDetails: !!this.detailsOpen[Number(st.Id)],
        };

        if (!this.scores[st.Id]) this.scores[st.Id] = {};
        if (!this.legacySubtotals[st.Id]) this.legacySubtotals[st.Id] = {};

        let final = 0;

        for (const p of this.periodsToDraw) {
          const periodId = Number(p.Id);
          if (!this.visiblePeriods[periodId]) continue;

          const sabers = this.periodSaberMap[periodId] || [];
          for (const s of sabers) {
            if (!this.visibleDimensions[s.Code]) continue;

            if (s.performances.length) {
              let weightedSum = 0;
              let wSum = 0;

              for (const c of s.performances) {
                const k = this.keyPerf(periodId, s.Code, c.Id);
                const raw = this.scores[st.Id][k];
                const v = typeof raw === "string" ? Number(raw) : (raw as number);
                const w = Number(c.percent) || 0;
                if (typeof v === "number" && !isNaN(v) && w > 0) {
                  const minB = this.minScore ?? 0;
                  const maxB = this.maxScore ?? 5;
                  const clamped = Math.max(minB, Math.min(maxB, v));
                  weightedSum += clamped * w;
                  wSum += w;
                }
                row[k] = typeof raw === "number" ? raw : (raw ?? undefined);
              }

              const subKey = this.keySubtotal(periodId, s.Code);
              const avg = wSum > 0 ? (weightedSum / wSum) : undefined;

              let subValue: number | undefined = avg == null ? undefined : Math.round((avg + Number.EPSILON) * 100) / 100;
              if (subValue == null) {
                const legacy = this.legacySubtotals[st.Id]?.[subKey];
                if (typeof legacy === "number" && !isNaN(legacy)) {
                  subValue = Math.round((legacy + Number.EPSILON) * 100) / 100;
                }
              }
              row[subKey] = subValue;

              if (row[subKey] != null) final += row[subKey];
            } else {
              const key = this.keyDirectSaber(periodId, s.Code);
              const raw = this.scores[st.Id][key];
              const v = typeof raw === "string" ? Number(raw) : (raw as number);
              const minB = this.minScore ?? 0;
              const maxB = this.maxScore ?? 5;
              const clamped = typeof v === "number" && !isNaN(v) ? Math.max(minB, Math.min(maxB, v)) : undefined;
              row[key] = clamped;
              if (row[key] != null) final += row[key];
            }
          }
        }

        row.FinalScore = Math.round((final + Number.EPSILON) * 100) / 100;
        return row;
      });
    },

    hasAnythingToSave(): boolean {
      return Object.values(this.scores).some((row) => Object.keys(row || {}).length > 0);
    },
    hasValidationErrors(): boolean {
      return Object.keys(this.errors).length > 0;
    },
  },

  async mounted() {
    window.addEventListener("online", this.onOnline);
    window.addEventListener("offline", this.onOffline);

    this.loadAutosaveSettings();
    await this.load();
    await this.loadEvaluationLimits();
    this.loadLocalDraft();

    await nextTick();
    this.buildFloatingHeader();
    this.syncFloatingHeader();

    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.syncFloatingHeader, { passive: true });

    const host = this.$refs.tableScroll as HTMLElement | undefined;
    if (host) host.addEventListener("scroll", this.onHScroll, { passive: true });

    window.addEventListener("beforeunload", this.beforeUnloadHandler);

    this.restartAutosaveTimer();
  },

  beforeUnmount() {
    window.removeEventListener("scroll", this.onScroll as any);
    window.removeEventListener("resize", this.syncFloatingHeader as any);
    window.removeEventListener("online", this.onOnline as any);
    window.removeEventListener("offline", this.onOffline as any);
    window.removeEventListener("beforeunload", this.beforeUnloadHandler as any);

    const host = this.$refs.tableScroll as HTMLElement | undefined;
    if (host) host.removeEventListener("scroll", this.onHScroll as any);

    this.stopAutosaveTimer();
  },

  watch: {
    selectedPeriodId(newVal: string) {
      if (!newVal) {
        this.selectedPeriodIds = (this.periods || []).map((p: any) => Number(p.Id));
      } else {
        const id = Number(newVal);
        if (!isNaN(id)) {
          this.selectedPeriodIds = [id];
        }
      }
    },

    selectedPeriodIds(newList: number[]) {
      if (!newList || !newList.length) {
        this.selectedPeriodId = "";
        return;
      }

      const allIds = (this.periods || []).map((p: any) => Number(p.Id));
      const allSelected =
        newList.length === allIds.length &&
        newList.every((id) => allIds.includes(Number(id)));

      if (allSelected) {
        this.selectedPeriodId = "";
      } else if (newList.length === 1) {
        this.selectedPeriodId = String(newList[0]);
      } else {
        this.selectedPeriodId = String(newList[0]);
      }
    },
  },

  methods: {
    /* ========== KEY HELPERS (period-aware) ========== */
    keyPerf(periodId: number, code: string, compOrPerfId: number) { return `P${periodId}.${code}.C${compOrPerfId}`; },
    keySubtotal(periodId: number, code: string) { return `P${periodId}.${code}.Subtotal`; },
    keyDirectSaber(periodId: number, code: string) { return `P${periodId}.${code}`; },

    /* ========== ERRORS ========== */
    cellKey(id: number, key: string) { return `${id}|${key}`; },
    cellError(id: number, key: string): string { return this.errors[this.cellKey(id, key)] || ""; },

    /* ========== VISIBILITY ========== */
    toggleSaber(code: string) { this.visibleDimensions[code] = !this.visibleDimensions[code]; },
    async toggleSaberAndSync(code: string) {
      this.toggleSaber(code);
      await nextTick();
      this.buildFloatingHeader();
      this.syncFloatingHeader();
    },
    togglePeriod(periodId: number) { this.visiblePeriods[periodId] = !this.visiblePeriods[periodId]; },
    async togglePeriodAndSync(periodId: number) {
      this.togglePeriod(periodId);
      await nextTick();
      this.buildFloatingHeader();
      this.syncFloatingHeader();
    },
    onSelectedPeriodChanged() {
      const selected = this.selectedPeriodId;
      const all = this.periods || [];

      const map: Record<number, boolean> = {};

      if (!selected) {
        for (const p of all) map[Number(p.Id)] = true;
      } else {
        const selId = Number(selected);
        for (const p of all) {
          const pid = Number(p.Id);
          map[pid] = pid === selId;
        }
      }

      this.visiblePeriods = map;

      this.$nextTick(() => {
        this.buildFloatingHeader();
        this.syncFloatingHeader();
      });
    },

    ensureStudentNotes(id: number) {
      if (!this.studentNotes[id]) {
        this.studentNotes = {
          ...this.studentNotes,
          [id]: { Observation: "", Recommendations: "" },
        };
      }
    },

    /* ✅ FIX: toggle persistente */
    toggleRowDetails(item: any) {
      if (!item) return;
      const id = Number(item.Id);
      if (!id) return;

      this.ensureStudentNotes(id);

      this.detailsOpen = {
        ...this.detailsOpen,
        [id]: !this.detailsOpen[id],
      };

      // (Opcional) fuerza repaint del header flotante
      this.$nextTick(() => this.syncFloatingHeader());
    },

    isDynamicValueField(key: string) { return this.dynamicValueFields.some((f) => f.key === key); },
    isSubtotalField(key: string) { return this.subtotalFields.some((f) => f.key === key); },

    /* ========== COLORS ========== */
    colorFromStr(seed: string) {
      const SABER_COLORS = [
        "#F8BBD0", "#F7C59F", "#FFE08A", "#CDEAC0", "#BDE0FE", "#CDB4DB", "#B8E0D2",
        "#FFAFCC", "#D4F1F4", "#FFD6A5", "#E6CCFF", "#B9FBC0", "#A0C4FF", "#FFC6FF",
      ];

      const s = (seed ?? "").toString().trim();
      if (!s) return SABER_COLORS[0];

      let h = 2166136261;
      for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619) >>> 0;
      }

      const idx = h % SABER_COLORS.length;
      return SABER_COLORS[idx];
    },

    compColor(c: { Id: number }) {
      const PERF_COLORS = [
        "#FDE2E4", "#FAD2E1", "#FFD6EA", "#FDE7F3", "#FFDCF5", "#F6D8FF", "#EBD5F5",
        "#F3E8FF", "#E9D7FE", "#EFD3F5", "#E6D0FF", "#F9DDE7", "#F7D6FF", "#F8D7EB",
        "#F2CCFF", "#EBD9FF", "#EED7FA", "#F5D9F6", "#FAD9E6", "#FCE0F1", "#F1D4F4",
        "#ECD9F2", "#FFE0F2", "#FDE1F0", "#F5D6ED", "#F2D7FF", "#EEDCFF", "#EAD6FF",
      ];
      const id = Number(c?.Id || 0);
      return PERF_COLORS[id >= 0 ? id % PERF_COLORS.length : 0];
    },

    /* ========== STYLES/TOOLTIPS (period-aware) ========== */
    getColStyle(fieldKey: string) {
      const m = /^P(\d+)\.([^.]+)(?:\.(.+))?$/.exec(fieldKey);
      if (!m) return {};
      const periodId = Number(m[1]);
      const code = m[2];
      const tail = m[3];

      if (!this.visiblePeriods[periodId]) return {};
      const sabers = this.periodSaberMap[periodId] || [];
      const s = sabers.find(x => x.Code === code);
      if (!s || !this.visibleDimensions[code]) return {};

      if (!tail || tail === "Subtotal") {
        return { background: s.color || "", padding: "0.08rem 0.20rem" };
      }
      if (tail.startsWith("C")) {
        const cid = Number(tail.substring(1));
        const c = s.performances.find(cc => Number(cc.Id) === cid);
        if (c) return { background: this.compColor(c) || s.color || "", padding: "0.08rem 0.20rem" };
      }
      return {};
    },
    getTooltip(fieldKey: string) {
      const m = /^P(\d+)\.([^.]+)(?:\.(.+))?$/.exec(fieldKey);
      if (!m) return "";
      const periodId = Number(m[1]);
      const code = m[2];
      const tail = m[3];

      if (!this.visiblePeriods[periodId]) return "";
      const sabers = this.periodSaberMap[periodId] || [];
      const s = sabers.find(x => x.Code === code);
      if (!s || !this.visibleDimensions[code]) return "";

      if (tail === "Subtotal") return `${s.Name}: promedio ponderado de desempeños`;
      if (!tail) return `${s.Name}: evaluación directa`;
      if (tail.startsWith("C")) {
        const cid = Number(tail.substring(1));
        const c = s.performances.find(cc => Number(cc.Id) === cid);
        if (c) return (c as any).Description || (c as any).Title;
      }
      return "";
    },

    /* ========== AUTOSAVE SETTINGS ========== */
    autosaveKey(): string {
      const subj = this.subjectId || "any";
      const grp = this.courseGroupId || "any";
      return `grades-autosave:ay=${this.academicYearId}:subj=${subj}:grp=${grp}`;
    },
    loadAutosaveSettings() {
      try {
        const raw = localStorage.getItem(this.autosaveKey());
        if (!raw) {
          this.autosave.enabled = true;
          this.autosave.minutes = 1;
          this.saveAutosaveSettings();
          return;
        }
        const cfg = JSON.parse(raw) as { enabled?: boolean; minutes?: number };
        this.autosave.enabled = cfg?.enabled !== false;
        this.autosave.minutes = 1;
      } catch {
        this.autosave.enabled = true;
        this.autosave.minutes = 1;
      }
    },
    saveAutosaveSettings() {
      try { localStorage.setItem(this.autosaveKey(), JSON.stringify({ enabled: this.autosave.enabled, minutes: 1 })); }
      catch (err: any) { this.notify("error " + err.message, "danger"); }
    },
    onAutosaveToggle() {
      this.saveAutosaveSettings();
      this.restartAutosaveTimer();
      this.notify("Auto-guardado " + (this.autosave.enabled ? "activado." : "desactivado."), "info");
    },
    restartAutosaveTimer() {
      this.stopAutosaveTimer();
      if (!this.autosave.enabled) return;
      const ms = 60 * 1000;
      this.autosaveTimer = setInterval(() => {
        if (this.hasAnythingToSave && !this.hasValidationErrors) this.saveLocalDraft(true);
      }, ms);
    },
    stopAutosaveTimer() { if (this.autosaveTimer) { clearInterval(this.autosaveTimer); this.autosaveTimer = null; } },

    /* ========== DRAFT HELPERS & DRAFT ========== */
    validFieldKeysSet(): Set<string> {
      const set = new Set<string>();
      for (const p of this.periodsToDraw) {
        const periodId = Number(p.Id);
        const sabers = this.periodSaberMap[periodId] || [];
        for (const s of sabers) {
          if (!s.performances?.length) { set.add(this.keyDirectSaber(periodId, s.Code)); continue; }
          for (const c of s.performances) set.add(this.keyPerf(periodId, s.Code, c.Id));
        }
      }
      return set;
    },
    allValidValueKeys(): Set<string> {
      const set = new Set<string>();
      for (const p of this.periods) {
        const periodId = Number(p.Id);
        const sabers = this.periodSaberMap[periodId] || [];
        for (const s of sabers) {
          if (!s.performances?.length) { set.add(this.keyDirectSaber(periodId, s.Code)); continue; }
          for (const c of s.performances) set.add(this.keyPerf(periodId, s.Code, c.Id));
        }
      }
      return set;
    },

    filteredCopyOfScoresForDraft(): Record<number, Record<string, number | string>> {
      const valid = this.validFieldKeysSet();
      const studentIds = new Set(this.students.map((s) => s.Id));
      const out: Record<number, Record<string, number | string>> = {};
      for (const sidStr of Object.keys(this.scores)) {
        const sid = Number(sidStr);
        if (!studentIds.has(sid)) continue;
        const row = this.scores[sid] || {};
        const dst: Record<string, number | string> = {};
        for (const [k, v] of Object.entries(row)) {
          if (!valid.has(k)) continue;
          if (typeof v === "number" || typeof v === "string") dst[k] = v;
        }
        if (Object.keys(dst).length) out[sid] = dst;
      }
      return out;
    },
    draftKey(): string {
      const subj = this.subjectId || "any";
      const grp = this.courseGroupId || "any";
      return `grades-draft:ay=${this.academicYearId}:subj=${subj}:grp=${grp}`;
    },
    onManualSaveDraft() {
      if (this.hasValidationErrors) { this.notify("Corrige las celdas inválidas antes de guardar el borrador.", "warning"); return; }
      this.saveLocalDraft(false);
    },
    saveLocalDraft(silent = true) {
      try {
        const nowIso = new Date().toISOString();
        const payload = { when: nowIso, schema: { fieldKeys: Array.from(this.validFieldKeysSet()) }, scores: this.filteredCopyOfScoresForDraft() };
        localStorage.setItem(this.draftKey(), JSON.stringify(payload));
        this.setDraftInfo(new Date(nowIso));
        if (!silent) this.notify("Borrador guardado localmente.", "success");
      } catch { if (!silent) this.notify("No se pudo guardar el borrador local.", "danger"); }
    },
    loadLocalDraft() {
      try {
        const raw = localStorage.getItem(this.draftKey());
        if (!raw) return;
        const parsed = JSON.parse(raw) as { when?: string };
        if (parsed?.when) this.setDraftInfo(new Date(parsed.when));
      } catch (err: any) { this.notify("error " + err.message, "danger"); }
    },
    restoreLocalDraftUI() {
      try {
        const raw = localStorage.getItem(this.draftKey());
        if (!raw) { this.notify("No se encontró ningún borrador para restaurar.", "warning"); return; }
        const parsed = JSON.parse(raw) as { when?: string; scores?: Record<string, Record<string, unknown>>; schema?: { fieldKeys?: string[] } };
        const draftScores: Record<string, Record<string, unknown>> = parsed?.scores ?? {};
        const draftFieldKeys: string[] = Array.isArray(parsed?.schema?.fieldKeys) ? (parsed!.schema!.fieldKeys as string[]) : [];
        const currentValid = this.validFieldKeysSet();
        const allowedKeys = new Set<string>(draftFieldKeys.length ? draftFieldKeys.filter((k) => currentValid.has(k)) : Array.from(currentValid));
        const restored: Record<number, Record<string, number | string>> = {};
        const currentIds = new Set(this.students.map((s) => s.Id));

        for (const sidStr of Object.keys(draftScores)) {
          const sid = Number(sidStr);
          if (!currentIds.has(sid)) continue;

          const row = draftScores[sidStr] || {};
          const out: Record<string, number | string> = {};
          for (const [k, v] of Object.entries(row)) {
            if (!allowedKeys.has(k)) continue;
            if (typeof v === "number" && Number.isFinite(v)) {
              const minB = this.minScore ?? 0, maxB = this.maxScore ?? 5;
              const n = Math.max(minB, Math.min(maxB, v));
              out[k] = Math.round((n + Number.EPSILON) * 100) / 100;
            } else if (typeof v === "string") out[k] = v;
            else if (v != null && typeof (v as any).toString === "function") out[k] = String(v);
            else out[k] = "";
          }
          if (Object.keys(out).length) restored[sid] = out;
        }

        this.scores = restored;

        this.$nextTick().then(() => {
          for (const st of this.students) this.recalcForStudent(st.Id);
          this.syncFloatingHeader();
        });

        this.setDraftInfo(parsed.when ? new Date(parsed.when) : new Date());
        this.notify("Borrador restaurado en la tabla.", "success");
      } catch (e) {
        console.error("restoreLocalDraftUI error", e);
        this.notify("No se pudo restaurar el borrador.", "danger");
      }
    },
    clearLocalDraft() {
      try { localStorage.removeItem(this.draftKey()); this.setDraftInfo(null); this.notify("Borrador local eliminado.", "secondary"); }
      catch (err: any) { this.notify("error " + err.message, "danger"); }
    },
    setDraftInfo(d: Date | null) {
      this.draft.savedAt = d;
      if (!d) { this.draft.savedAtShort = ""; this.draft.savedAtTitle = ""; return; }

      const fmt = new Intl.DateTimeFormat("es-CO", {
        timeZone: "America/Bogota", year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hour12: false,
      });
      const local = fmt.format(d);
      const normalized = local.replace(", ", " ");
      this.draft.savedAtShort = normalized;
      this.draft.savedAtTitle = `Guardado: ${normalized} (hora de Colombia)`;
    },
    beforeUnloadHandler() { if (this.hasAnythingToSave && !this.hasValidationErrors) this.saveLocalDraft(true); },

    /* ========== HARD ENFORCEMENT (inputs) ========== */
    numericKeyGuard(evt: KeyboardEvent) {
      const allowed = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End", "Enter"];
      const isCtrl = evt.ctrlKey || evt.metaKey;
      const key = evt.key;
      if (isCtrl && ["a", "c", "v", "x", "z", "y"].includes(key.toLowerCase())) return;
      if (allowed.includes(key)) return;
      if (/^[0-9.]$/.test(key)) return;
      evt.preventDefault();
    },
    onCellInputHard(id: number, key: string, e: Event) {
      const input = e.target as HTMLInputElement | null;
      const vRaw = input ? input.value : "";
      let v = (vRaw || "").trim();
      if (v === "") { delete this.errors[this.cellKey(id, key)]; this.scores[id][key] = ""; return; }
      if ((v.match(/\./g) || []).length > 1 || v === "." || v === "-." || v === "-") { this.hardReject(id, key, input); return; }

      const num = Number(v);
      const minB = this.minScore ?? 0, maxB = this.maxScore ?? 5;
      if (!Number.isFinite(num) || num < minB || num > maxB) { this.hardReject(id, key, input, minB, maxB); return; }

      delete this.errors[this.cellKey(id, key)];
      this.scores[id][key] = v;
    },
    onCellCommit(id: number, key: string) {
      const raw = this.scores[id]?.[key];
      const minB = this.minScore ?? 0, maxB = this.maxScore ?? 5;
      if (raw === "" || raw == null) return;
      const n = typeof raw === "string" ? Number(raw) : (raw as number);
      if (!Number.isFinite(n) || n < minB || n > maxB) {
        const el = this.getCellEl(id, key); this.hardReject(id, key, el || undefined, minB, maxB); return;
      }
      const rounded = Math.round((n + Number.EPSILON) * 100) / 100;
      this.scores[id][key] = String(rounded);
      this.recalcForStudent(id);
      this.debounce(() => this.saveLocalDraft(true), 800)();
    },
    hardReject(id: number, key: string, input?: HTMLInputElement | null, minB?: number, maxB?: number) {
      const lo = (minB ?? this.minScore ?? 0).toFixed(2), hi = (maxB ?? this.maxScore ?? 5).toFixed(2);
      this.errors[this.cellKey(id, key)] = `Valor inválido. Ingresa un número entre ${lo} y ${hi}.`;
      if (input) input.value = "";
      this.scores[id][key] = "";
      this.notify(`El valor ingresado no es válido.\nDebes ingresar un número entre ${lo} y ${hi}.`, "danger");
      this.$nextTick(() => {
        const el = input || this.getCellEl(id, key); if (el) {
          el.focus(); try { el.select?.(); } catch { console.log("hardReject select error"); }
        }
      });
    },
    isOutOfRange(val: number | string | undefined) {
      const minB = this.minScore ?? 0, maxB = this.maxScore ?? 5;
      const v = typeof val === "string" ? Number(val) : val;
      return typeof v === "number" && (v < minB || v > maxB);
    },

    cellRef(id: number, key: string) { return `cell-${id}-${key}`; },
    getCellEl(id: number, key: string): HTMLInputElement | null {
      const r = (this.$refs as any)[this.cellRef(id, key)];
      if (!r) return null;
      return (Array.isArray(r) ? r[0] : r) as HTMLInputElement;
    },
    focusCell(id: number, key: string) {
      this.$nextTick(() => {
        const el = this.getCellEl(id, key); if (el) {
          el.focus(); try { el.select?.(); } catch { console.log("focusCell select error"); }
        }
      });
    },
    blockIfError(id: number, key: string, evt: KeyboardEvent) {
      if (this.cellError(id, key)) { evt.preventDefault(); this.focusCell(id, key); }
      else if (evt.key === "Tab") {
        const dir = (evt as any).shiftKey ? -1 : 1; this.moveFocus(id, key, dir);
      }
    },
    enforceIfInvalid(id: number, key: string) { if (this.cellError(id, key)) { setTimeout(() => this.focusCell(id, key), 0); } },
    moveFocus(id: number, key: string, dir: 1 | -1) {
      const visibleKeys: string[] = [];
      for (const p of this.periodsToDraw) {
        const periodId = Number(p.Id);
        if (!this.visiblePeriods[periodId]) continue;
        const sabers = this.periodSaberMap[periodId] || [];
        for (const s of sabers) {
          if (!this.visibleDimensions[s.Code]) continue;
          if (s.performances.length) { for (const c of s.performances) visibleKeys.push(this.keyPerf(periodId, s.Code, c.Id)); }
          else { visibleKeys.push(this.keyDirectSaber(periodId, s.Code)); }
        }
      }
      const colIndex = visibleKeys.indexOf(key); if (colIndex < 0) return;
      const studentIds = this.students.map((s) => s.Id);
      const rowIndex = studentIds.indexOf(id); if (rowIndex < 0) return;

      let nextCol = colIndex + dir, nextRow = rowIndex;
      if (nextCol < 0) { nextRow = Math.max(0, rowIndex - 1); nextCol = visibleKeys.length - 1; }
      else if (nextCol >= visibleKeys.length) { nextRow = Math.min(studentIds.length - 1, rowIndex + 1); nextCol = 0; }

      const nextId = studentIds[nextRow], nextKey = visibleKeys[nextCol];
      this.focusCell(nextId, nextKey);
    },

    _debounceTimer: undefined as any,
    debounce(fn: () => void, ms = 500) {
      return () => { if (this._debounceTimer) clearTimeout(this._debounceTimer); this._debounceTimer = setTimeout(fn, ms); };
    },

    /* ========== CONNECTIVITY ========== */
    onOnline() { this.navigatorOnline = true; },
    onOffline() { this.navigatorOnline = false; },

    /* ========== SAVE TO SERVER (per-cell period, not forced to current) ========== */
    codeToSaberId(): Record<string, number> {
      const map: Record<string, number> = {};
      for (const s of this.baseDimensionList) map[s.Code] = s.Id;
      return map;
    },

    buildServerPayload() {
      const codeToId = this.codeToSaberId();
      const validKeys = this.allValidValueKeys();

      const out: any[] = [];
      for (const st of this.students) {
        const row = this.scores[st.Id] || {};

        const notes = this.studentNotes[st.Id] || { Observation: "", Recommendations: "" };
        for (const [k, raw] of Object.entries(row)) {
          if (!validKeys.has(k)) continue;

          const existingId = this.scoreIds?.[st.Id]?.[k] || 0;

          const score = typeof raw === "string" ? Number(raw) : (raw as number);
          if (!Number.isFinite(score)) continue;

          const m = /^P(\d+)\.([^.]+)(?:\.(.+))?$/.exec(k);
          if (!m) continue;

          const periodId = Number(m[1]);
          const code = m[2];
          const tail = m[3];

          const DimensionTypeId = codeToId[code];
          if (!DimensionTypeId) continue;

          let PerformanceId: number | null = null;
          if (tail && tail.startsWith("C")) {
            const pid = Number(tail.substring(1));
            PerformanceId = Number.isFinite(pid) ? pid : null;
          }
          out.push({
            Id: existingId,
            StudentId: st.Id,
            PeriodId: periodId,
            TeacherId: this.teacherId,
            DimensionId: DimensionTypeId,
            AcademicYearId: this.academicYearId,
            PerformanceId: PerformanceId,
            Observation: notes.Observation || "",
            Recommendations: notes.Recommendations || "",
            Score: Math.max(this.minScore ?? 0, Math.min(this.maxScore ?? 5, score)),
            SubjectId: this.subjectId,
            GroupId: this.courseGroupId,
          });
        }
      }
      return out;
    },

    async persistToServer() {
      if (this.hasValidationErrors) { this.notify("Hay celdas inválidas. Corrígelas antes de guardar.", "warning"); return; }
      if (!this.navigatorOnline) { this.notify("Sin conexión. Guarda como borrador y vuelve a intentar cuando estés en línea.", "warning"); return; }
      const payload = this.buildServerPayload();
      if (!payload.length) { this.notify("No hay cambios para guardar.", "secondary"); return; }

      this.saving = true;
      try {
        this.saveLocalDraft(true);
        await Promise.all(payload.map((p: any) => (PreschoolEvaluationsService as any).Post(p)));
        this.notify("Registro completado con éxito.", "success");
        // location.reload();
      } catch (err: any) {
        console.error("persistToServer error:", err);
        this.notify("No se pudo guardar en el servidor. Se conservó el borrador local.", "danger");
      } finally { this.saving = false; }
    },

    /* ========== PERIOD RESOLUTION (Current + Open Window) ========== */
    async resolveCurrentPeriodForYear(academicYearId: number) {
      let current: any | null = null;
      try {
        current = await (PeriodsService as any).GetCurrentPeriodByAcademicYear({ AcademicYearId: academicYearId });
      } catch {
        current = null;
      }

      const now = new Date();
      const inWindow = (p: any) => {
        const open = p ? p.IsOpen : false;
        const from = p?.OpenFrom ? new Date(p.OpenFrom) : null;
        const to = p?.OpenTo ? new Date(p.OpenTo) : null;
        const afterFrom = from ? now >= from : true;
        const beforeTo = to ? now <= to : true;
        return open && afterFrom && beforeTo;
      };

      const fmtCO = (d?: string | Date | null) => {
        if (!d) return null;
        const dt = typeof d === "string" ? new Date(d) : d;
        const fmt = new Intl.DateTimeFormat("es-CO", {
          timeZone: "America/Bogota",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
        return fmt.format(dt).replace(", ", " ");
      };

      if (current && current.Id) {
        this.periodId = Number(current.Id);
        this.canEdit = inWindow(current);
        this.periodGate = {
          id: current.Id,
          number: current.PeriodNumber,
          description: current.Description || "",
          openFrom: fmtCO(current.OpenFrom),
          openTo: fmtCO(current.OpenTo),
        };
      } else {
        this.periodId = 0;
        this.canEdit = false;
        this.periodGate = { id: 0, number: 0, description: "", openFrom: null, openTo: null };
      }
    },

    /* ========== LOAD ========== */
    async load() {
      this.loading = true;
      try {
        this.scores = {};
        this.legacySubtotals = {};
        this.scoreIds = {};

        this.groups = await CourseGroupsService.GetCourseGroups() as ICourseGroupsModel[];
        let _teacherRec = await TeachersService.GetTeachers() as ITeachersModel[];

        const assign = await PreschoolTeacherAssignmentService.GetById(this.assignmentId as any);
        if (!assign) { this.notify("No se encontró el registro de AcademicTeacherAssigment.", "danger"); return; }

        this.courseGroupId = Number(assign.GroupId);

        this.academicYearId = Number(assign.AcademicYearId);
        this.teacherId = Number(assign.TeacherId);

        this.teacher = _teacherRec.find(t => t.Id === this.teacherId) as ITeachersModel;

        await this.resolveCurrentPeriodForYear(this.academicYearId);

        const enrollments = await IndividualEnrollmentService.GetEnrollmentsByGroupYear({
          GroupId: this.courseGroupId,
          AcademicYearId: this.academicYearId,
        });
        this.enrollments = enrollments || [];
        const enrolledIds = new Set(this.enrollments.map((e) => e.StudentId));

        const periods = await PeriodsService.GetPeriodsByAcademicYear({ AcademicYearId: this.academicYearId } as any) as any;

        const [
          allStudents,
          dimensionRaw,
          gradesAll,
          assignmentsAll,
          performancesRaw,
        ] = await Promise.all([
          StudentsService.GetStudents(),
          PreschoolDimensionsService.GetPreschoolDimensions(),
          (async () => {
            const svc: any = PreschoolEvaluationsService as any;
            if (typeof svc.GetByYear === "function") {
              return await svc.GetByYear({
                TeacherId: this.teacherId,
                GroupId: this.courseGroupId,
                AcademicYearId: this.academicYearId,
              } as any);
            }
            const perLists = await Promise.all(
              (periods || []).map((p: any) =>
                PreschoolEvaluationsService.GetByPeriodId({
                  PeriodId: Number(p.Id),
                  TeacherId: this.teacherId,
                  GroupId: this.courseGroupId,
                } as any)
              )
            );
            return ([] as any[]).concat(...perLists);
          })(),
          (async () => {
            const perLists = await Promise.all(
              (periods || []).map((p: any) =>
                PreschoolPerformancePeriodAssignmentsService.GetByPeriodId(Number(p.Id))
              )
            );
            return ([] as any[]).concat(...perLists);
          })(),
          PreschoolPerformancesService.GetPreschoolPerformances(),
        ]);

        const studentsAll = (allStudents || []) as IStudentsModel[];
        this.students = studentsAll
          .filter((s) => enrolledIds.has(s.Id))
          .sort((a, b) => (`${a.Name} ${a.LastName || ""}`).localeCompare(`${b.Name} ${b.LastName || ""}`));

        const notesInit: Record<number, { Observation: string; Recommendations: string }> = {};
        const detailsInit: Record<number, boolean> = {};

        for (const st of this.students) {
          this.scores[st.Id] = {};
          this.scoreIds[st.Id] = {};
          this.legacySubtotals[st.Id] = {};
        }

        this.studentNotes = notesInit;
        this.detailsOpen = detailsInit;

        const dimensions = (dimensionRaw || []) as IPreschoolDimensionsModel[];
        const competencies = (performancesRaw || []) as IPreschoolPerformancesModel[];
        const grades = (gradesAll || []) as IPreschoolEvaluationsModel[];
        const assignments = (assignmentsAll || []) as any[];
        const performances = (performancesRaw || []) as IPreschoolPerformancesModel[];

        this.periods = periods || [];

        const baseDimensionList: DimensionUi[] = dimensions
          .map((s) => ({
            ...s,
            color: (s as any).Color || this.colorFromStr(s.Code),
            performances: [],
          }));

        this.baseDimensionList = baseDimensionList;

        const dimensionVisible: Record<string, boolean> = {};
        for (const s of baseDimensionList) dimensionVisible[s.Code] = true;
        this.visibleDimensions = dimensionVisible;

        const periodVisible: Record<number, boolean> = {};
        const currentPid = Number(this.periodId || 0);

        if (currentPid && (this.periods || []).some((p: any) => Number(p.Id) === currentPid)) {
          for (const p of this.periods) {
            const pid = Number(p.Id);
            periodVisible[pid] = pid === currentPid;
          }
          this.selectedPeriodId = String(currentPid);
        } else {
          for (const p of this.periods) {
            const pid = Number(p.Id);
            periodVisible[pid] = true;
          }
          this.selectedPeriodId = "";
        }

        this.visiblePeriods = periodVisible;

        const performanceById = new Map<number, IPreschoolPerformancesModel>();
        for (const c of competencies) performanceById.set(c.Id, c);

        const periodSaberMap: Record<number, DimensionUi[]> = {};

        for (const p of this.periods) {
          const periodId = Number(p.Id);

          const dimensionForPeriod: DimensionUi[] = baseDimensionList.map(bs => ({
            ...bs,
            competencies: [],
          }));

          const activeAssignmentIds = new Set<number>(
            (assignments || [])
              .filter((a: any) => Number(a.PeriodId) === periodId)
              .map((a: any) => Number(a.PerformanceId))
          );

          const assignWeightByPeriodPerf = new Map<string, number>();
          (assignments || []).forEach((a: any) => {
            const key = `${Number(a.PeriodId)}:${Number(a.PerformanceId)}`;
            assignWeightByPeriodPerf.set(key, Number(a.WeightPercent) || 0);
          });

          const perfCols = (performances || [])
            .map((perf) => {
              const comp = performanceById.get(perf.Id);
              if (!comp) return null;

              if ((assignments || []).length && !activeAssignmentIds.has(Number(perf.Id))) return null;

              const comboDesc = [comp.Code, comp.Description].filter(Boolean).join(" — ");

              const percentFromAssignment =
                assignWeightByPeriodPerf.get(`${periodId}:${Number(perf.Id)}`);

              return {
                perfId: perf.Id,
                performance: comp,
                percent: Number(
                  percentFromAssignment ??
                  (typeof (perf as any).WeightPercent !== "undefined" ? (perf as any).WeightPercent : 0)
                ),
                DisplayCode: String((perf as any).DisplayCode ?? comp.Id),
                Description: comboDesc || comp.Description,
              };
            })
            .filter(Boolean) as Array<{
              perfId: number;
              performance: IPreschoolPerformancesModel;
              percent: number;
              DisplayCode: string;
              Description: string;
            }>;

          for (const s of dimensionForPeriod) {
            const performancesForDimensions = perfCols
              .filter(pc => pc.performance.DimensionId === s.Id)
              .map(pc => ({
                ...(pc.performance as any),
                Id: pc.perfId,
                percent: pc.percent,
                DisplayCode: pc.DisplayCode,
                Title: pc.Description,
                Description: pc.Description,
              })) as unknown as Array<IPreschoolPerformancesModel & { percent: number; DisplayCode: string }>;

            performancesForDimensions.sort((a, b) =>
              a.Description === b.Description
                ? String(a.DisplayCode).localeCompare(String(b.DisplayCode))
                : String(a.Description).localeCompare(String(b.Description))
            );

            s.performances = performancesForDimensions;
          }

          periodSaberMap[periodId] = dimensionForPeriod;
        }

        this.periodSaberMap = periodSaberMap;

        this.scores = {};
        this.legacySubtotals = {};
        for (const st of this.students) {
          this.scores[st.Id] = {};
          this.legacySubtotals[st.Id] = {};
        }

        const dimensionCodeById = new Map<number, string>();
        dimensions.forEach((s) => dimensionCodeById.set(s.Id, s.Code));

        for (const g of (grades || [])) {
          const periodId = Number(g.PeriodId);
          const dimensionCode = dimensionCodeById.get(g.DimensionId);
          if (!dimensionCode) continue;
          if (!enrolledIds.has(g.StudentId)) continue;

          if ((g as any).Observation || (g as any).Recommendations) {
            this.ensureStudentNotes(Number(g.StudentId));
            if (!(this.studentNotes[g.StudentId].Observation || "").trim() && (g as any).Observation) {
              this.studentNotes[g.StudentId].Observation = String((g as any).Observation);
            }
            if (!(this.studentNotes[g.StudentId].Recommendations || "").trim() && (g as any).Recommendations) {
              this.studentNotes[g.StudentId].Recommendations = String((g as any).Recommendations);
            }
          }

          const dimensionForPeriod = this.periodSaberMap[periodId] || [];
          const sForCode = dimensionForPeriod.find(x => x.Code === dimensionCode);
          const hasPerformances = !!sForCode && sForCode.performances && sForCode.performances.length > 0;

          const sid = Number(g.StudentId);
          const evalId = Number((g as any).Id || 0);

          if (!g.PerformanceId) {
            if (hasPerformances) {
              // This is legacy subtotal record (not an editable performance cell)
              const subKey = this.keySubtotal(periodId, dimensionCode);
              this.legacySubtotals[sid][subKey] = Number(g.Score);

              // If you also want to track its Id, you can store it here too:
              // this.scoreIds[sid][subKey] = evalId;
            } else {
              // Direct saber cell (editable)
              const key = this.keyDirectSaber(periodId, dimensionCode);
              this.scores[sid][key] = Number(g.Score);
              if (evalId > 0) this.scoreIds[sid][key] = evalId; // ✅
            }
          } else {
            // Performance cell (editable)
            const key = this.keyPerf(periodId, dimensionCode, Number(g.PerformanceId));
            this.scores[sid][key] = Number(g.Score);
            if (evalId > 0) this.scoreIds[sid][key] = evalId; // ✅
          }

        }
      } finally {
        this.loading = false;
      }
    },

    /* ========== METRICS ========== */
    async loadEvaluationLimits() {
      try {
        const res = await SystemEvaluationMetricsService.GetEvaluationMetrics();
        const all = Array.isArray(res) ? res : [];
        const enabled = all
          .map((x: ISystemEvaluationMetricsModel) => ({
            ...x,
            MinValue: Number(x.MinValue),
            MaxValue: Number(x.MaxValue),
            Enable: Boolean((x as any).Enable ?? x.Enable),
          }))
          .filter((x) => x.Enable && Number.isFinite(x.MinValue) && Number.isFinite(x.MaxValue))
          .sort((a, b) => a.MinValue - b.MinValue);

        this.metrics = enabled;

        const gMin = enabled.length ? Math.min(...enabled.map((x) => x.MinValue)) : 0;
        const gMax = enabled.length ? Math.max(...enabled.map((x) => x.MaxValue)) : 5;

        this.minScore = Number.isFinite(gMin) ? gMin : 0;
        this.maxScore = Number.isFinite(gMax) ? gMax : 5;

        if ((this.maxScore as number) <= (this.minScore as number)) {
          this.minScore = 0; this.maxScore = 5;
        }
      } catch (e) {
        console.warn("GetSystemEvaluationMetrics failed, using defaults 0..5", e);
        this.metrics = []; this.minScore = 0; this.maxScore = 5;
      }
    },

    /* ========== RECALC / UI HELPERS / FLOATING HEADER ========== */
    async recalcForStudent(id: number) { this.scores = { ...this.scores }; await nextTick(); this.syncFloatingHeader(); },

    openPhoto(url?: string | null, initials = "", name = "") {
      this.photo.url = url || null; this.photo.initials = initials; this.photo.name = name; this.photo.open = true;
    },
    formatScore(v?: number) { return v == null ? "—" : Number(v).toFixed(2); },
    notify(text: string, type: "success" | "danger" | "warning" | "info" | "secondary") {
      const typeMap: any = { success: "success", danger: "error", warning: "warning", info: "info", secondary: "info" };
      store.dispatch("core/popupDialog", { text, type: typeMap[type] ?? "info", showCancelButton: false });
    },

    buildFloatingHeader() {
      const tableEl = (this.$refs.scoresTable as any)?.$el as HTMLElement | undefined;
      if (!tableEl) return;
      const liveTop = tableEl.querySelector("thead tr.thead-top-row") as HTMLTableRowElement | null;
      const liveMain = tableEl.querySelector("thead tr.thead-main-row") as HTMLTableRowElement | null;
      const mountTop = this.$refs.floatTopRow as HTMLTableRowElement;
      const mountMain = this.$refs.floatMainRow as HTMLTableRowElement;

      const clear = (el: HTMLElement) => { while (el.firstChild) el.removeChild(el.firstChild); };
      const cloneRowCells = (src: HTMLTableRowElement, dst: HTMLTableRowElement) => {
        const cells = Array.from(src.children) as HTMLTableCellElement[];
        for (const c of cells) {
          const clone = c.cloneNode(true) as HTMLTableCellElement;
          clone.classList.remove("sticky-col-left", "z-3");
          const css = clone.style as CSSStyleDeclaration;
          css.position = ""; css.left = ""; css.right = ""; css.zIndex = "";
          dst.appendChild(clone);
        }
      };

      clear(mountTop); clear(mountMain);
      if (liveTop) cloneRowCells(liveTop, mountTop); else { const th = document.createElement("th"); th.textContent = "Estudiante"; mountTop.appendChild(th); }
      if (liveMain) cloneRowCells(liveMain, mountMain);
    },

    syncFloatingHeader() {
      const tableEl = (this.$refs.scoresTable as any)?.$el as HTMLElement | undefined;
      const host = this.$refs.floatHost as HTMLElement | undefined;
      const scrollHost = this.$refs.tableScroll as HTMLElement | undefined;
      const floatInner = this.$refs.floatInner as HTMLElement | undefined;
      if (!tableEl || !host || !scrollHost || !floatInner) return;

      const rect = scrollHost.getBoundingClientRect();
      const docLeft = rect.left + window.scrollX;
      const width = Math.floor(rect.width);

      this.float.style = { left: `${docLeft}px`, width: `${width}px`, top: `var(--app-fixed-offset, 0px)` };

      floatInner.style.transform = `translateX(-${scrollHost.scrollLeft}px)`;

      const liveMain = tableEl.querySelector("thead tr.thead-main-row") as HTMLTableRowElement | null;
      const liveTop = tableEl.querySelector("thead tr.thead-top-row") as HTMLTableRowElement | null;
      const cloneMain = this.$refs.floatMainRow as HTMLTableRowElement;
      const cloneTop = this.$refs.floatTopRow as HTMLTableRowElement;

      if (liveMain && cloneMain) {
        const liveThs = Array.from(liveMain.children) as HTMLElement[];
        const cloneThs = Array.from(cloneMain.children) as HTMLElement[];
        const len = Math.min(liveThs.length, cloneThs.length);
        for (let i = 0; i < len; i++) {
          const w = liveThs[i].getBoundingClientRect().width;
          cloneThs[i].style.minWidth = `${w}px`;
          cloneThs[i].style.width = `${w}px`;
          cloneThs[i].style.maxWidth = `${w}px`;
          cloneThs[i].style.background = (liveThs[i] as any).style?.background || "";
        }
      }

      if (liveTop && cloneTop && liveMain) {
        const mainThs = Array.from(liveMain.children) as HTMLElement[];
        const liveTopThs = Array.from(liveTop.children) as HTMLTableCellElement[];
        const cloneTopThs = Array.from(cloneTop.children) as HTMLTableCellElement[];

        const sumWidth = (start: number, span: number) => {
          let sum = 0;
          for (let i = start; i < start + span && i < mainThs.length; i++) sum += mainThs[i].getBoundingClientRect().width;
          return sum;
        };

        let mainIdx = 0;
        for (let i = 0; i < cloneTopThs.length; i++) {
          const span = liveTopThs[i]?.colSpan || 1;
          const totalW = sumWidth(mainIdx, span);
          mainIdx += span;

          const cth = cloneTopThs[i];
          cth.style.minWidth = `${totalW}px`;
          cth.style.width = `${totalW}px`;
          cth.style.maxWidth = `${totalW}px`;
          cth.style.background = liveTopThs[i]?.style.background || "";
        }
      }

      this.onScroll();
    },

    onHScroll() {
      const scrollHost = this.$refs.tableScroll as HTMLElement | undefined;
      const floatInner = this.$refs.floatInner as HTMLElement | undefined;
      if (!scrollHost || !floatInner) return;
      floatInner.style.transform = `translateX(-${scrollHost.scrollLeft}px)`;
    },

    onScroll() {
      const tableEl = (this.$refs.scoresTable as any)?.$el as HTMLElement | undefined;
      const host = this.$refs.floatHost as HTMLElement | undefined;
      const scrollHost = this.$refs.tableScroll as HTMLElement | undefined;
      if (!tableEl || !host || !scrollHost) return;

      const rect = tableEl.getBoundingClientRect();
      const topOffset = this.getFixedTopOffset();

      const within = rect.top < topOffset && rect.bottom > topOffset + 10;
      this.float.visible = within;

      const srect = scrollHost.getBoundingClientRect();
      const docLeft = srect.left + window.scrollX;
      host.style.left = `${docLeft}px`;
      host.style.width = `${Math.floor(srect.width)}px`;
    },

    getFixedTopOffset() {
      const v = getComputedStyle(document.documentElement).getPropertyValue("--app-fixed-offset").trim();
      const n = parseInt(v || "0", 10);
      return isNaN(n) ? 0 : n;
    },
  },
});
